import SEO from "@/components/SEO";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "wouter";
import { useQuoteDrawer } from "@/contexts/QuoteDrawerContext";
import { Search, TrendingUp, Target, BarChart, Zap, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useState } from "react";
import { RelatedServices } from "@/components/RelatedServices";
import { Monitor, Smartphone, PenTool } from "lucide-react";
import WebDesignLocations from '@/components/WebDesignLocations';

export default function SEOServices() {
  const { openDrawer } = useQuoteDrawer();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  const services = [
    {
      title: "On-Page SEO",
      description: "Optimize your website content, meta tags, and structure for better search rankings.",
      icon: <Search className="h-8 w-8 text-blue-400" />
    },
    {
      title: "Technical SEO",
      description: "Improve site speed, mobile responsiveness, and technical search factors.",
      icon: <Target className="h-8 w-8 text-green-400" />
    },
    {
      title: "Content Strategy",
      description: "Create SEO-optimized content that attracts and engages your target audience.",
      icon: <TrendingUp className="h-8 w-8 text-purple-400" />
    },
    {
      title: "SEO Analytics",
      description: "Track rankings, traffic, and conversions with comprehensive SEO reporting.",
      icon: <BarChart className="h-8 w-8 text-yellow-400" />
    }
  ];

  return (
    <>
      <SEO 
        title="Professional SEO Services UAE | Kecly"
        description="Boost your search rankings in Dubai & Sharjah. Our data-driven SEO strategies deliver organic traffic growth."
        keywords="seo services uae, search engine optimization dubai, organic traffic sharjah"
      />

      <main className="bg-white text-gray-900">
        <section className="py-20 bg-gradient-to-br from-blue-50 to-indigo-50 overflow-hidden">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Search Engine <span className="text-blue-600">Dominance</span>
            </h1>
            <p className="text-xl max-w-3xl mx-auto mb-8">
              Data-driven SEO strategies to grow your organic traffic
            </p>
          </div>
        </section>

        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-blue-50 to-indigo-50 py-32 px-6 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('/grid.svg')] bg-center [mask-image:linear-gradient(180deg,white,rgba(255,255,255,0))]"></div>
          </div>
          
          <div className="max-w-7xl mx-auto relative z-10">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                <span className="text-blue-600">SEO</span> Services That Drive Results
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                Increase your visibility and organic traffic with our data-driven SEO strategies.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  onClick={openDrawer}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 text-lg font-medium rounded-lg"
                >
                  Get Free Audit
                </Button>
                <Link href="/portfolio">
                  <Button 
                    variant="outline"
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 px-8 py-3 text-lg font-medium rounded-lg"
                  >
                    See Case Studies
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="container mx-auto px-4 sm:px-6">
            <motion.div 
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Comprehensive <span className="text-blue-600">SEO Solutions</span>
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We cover all aspects of search engine optimization to maximize your online presence.
              </p>
            </motion.div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                  className="group bg-gray-50 p-6 rounded-xl border border-gray-200 hover:border-blue-400 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-700 mb-5 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                  <p className="text-gray-600">{service.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SEO Process */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Our SEO Process</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Systematic approach to improve your search visibility</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {[
                { step: "01", title: "SEO Audit", description: "Comprehensive website and competitor analysis" },
                { step: "02", title: "Strategy", description: "Custom SEO strategy based on your goals" },
                { step: "03", title: "Implementation", description: "Execute on-page and technical optimizations" },
                { step: "04", title: "Reporting", description: "Track progress and adjust strategies" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                    <span className="font-orbitron font-bold text-white">{item.step}</span>
                  </div>
                  <h3 className="font-orbitron font-semibold text-lg mb-2 text-gray-900">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-6">
            <motion.div 
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Measurable Results</h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">Our SEO strategies deliver consistent improvements</p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
              {[
                { metric: "400%", label: "Organic Traffic Increase" },
                { metric: "75%", label: "Keywords in Top 10" },
                { metric: "250%", label: "Lead Generation" },
                { metric: "90%", label: "Client Retention" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white text-gray-900 p-8 rounded-xl hover:shadow-lg hover:shadow-blue-500/20 transition-all text-center"
                >
                  <div className="font-orbitron font-bold text-4xl text-blue-400 mb-2">{stat.metric}</div>
                  <div className="text-gray-600">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <RelatedServices 
              currentService="SEO" 
              services={[
                {
                  title: "Web Design & Development",
                  description: "Custom websites and web applications tailored to your business needs.",
                  icon: <Monitor className="h-8 w-8" />
                },
                {
                  title: "Digital Marketing",
                  description: "Comprehensive digital marketing strategies to grow your online presence.",
                  icon: <TrendingUp className="h-8 w-8" />
                },
                {
                  title: "Content Strategy",
                  description: "Create engaging content that attracts and retains your target audience.",
                  icon: <PenTool className="h-8 w-8" />
                }
              ]} 
            />
          </div>
        </section>

        {/* Web Design Locations */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <WebDesignLocations />
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-6 text-center">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="font-orbitron font-bold text-4xl text-gray-900 mb-6">Ready to Improve Your Search Rankings?</h2>
              <p className="text-xl text-blue-600 mb-10 max-w-3xl mx-auto">
                Let's develop an SEO strategy that drives qualified traffic to your website
              </p>
              <Button 
                size="lg" 
                className="bg-blue-600 text-white hover:bg-blue-700 font-medium"
                onClick={openDrawer}
              >
                Get Started Today
              </Button>
            </motion.div>
          </div>
        </section>
      </main>
    </>
  );
}
